import { useState, useCallback, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import type { Truck } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { EQUIPMENT_TYPES } from "@/lib/equipmentTypes";

async function fetchLocationData(zip: string): Promise<{ city: string; state: string } | null> {
  if (!/^\d{5}$/.test(zip)) return null;
  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.places && data.places.length > 0) {
      return {
        city: data.places[0]["place name"],
        state: data.places[0]["state abbreviation"],
      };
    }
    return null;
  } catch (error) {
    return null;
  }
}

interface EditTruckModalProps {
  truck: Truck | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

function formatDateForInput(date: Date | string | null): string {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
}

function parseDimensions(dimensions: string | null): { length: string; width: string; height: string } {
  if (!dimensions || dimensions === "Standard") return { length: "", width: "", height: "" };
  const parts = dimensions.split(" x ").map(s => s.trim());
  return {
    length: parts[0] || "",
    width: parts[1] || "",
    height: parts[2] || "",
  };
}

export default function EditTruckModal({ truck, open, onOpenChange, onSuccess }: EditTruckModalProps) {
  const { toast } = useToast();

  const getInitialFormData = useCallback(() => {
    if (!truck) return null;
    const dims = parseDimensions(truck.dimensions);
    const resolveEquipmentType = (raw: string | null | undefined): string => {
      if (!raw) return "";
      if ((EQUIPMENT_TYPES as readonly string[]).includes(raw)) return raw;
      if (raw.toLowerCase().startsWith("hotshot")) return "Hotshot";
      return "";
    };
    return {
      originZip: truck.originZip || "",
      originCity: truck.originCity || "",
      originState: truck.originState || "",
      destinationZip: truck.destinationZip || "",
      destinationCity: truck.destinationCity || "",
      destinationState: truck.destinationState || "",
      capacity: truck.capacity?.toString() || "",
      availableFrom: formatDateForInput(truck.availableFrom),
      availableTo: formatDateForInput(truck.availableTo),
      dimensionLength: dims.length,
      dimensionWidth: dims.width,
      dimensionHeight: dims.height,
      description: truck.description || "",
      equipmentType: resolveEquipmentType(truck.equipmentType),
      availablePallets: truck.availablePallets?.toString() || "",
      availableWeight: truck.availableWeight?.toString() || "",
      availableLinearFeet: truck.availableLinearFeet?.toString() || "",
      acceptsStackable: truck.acceptsStackable ?? true,
      acceptsHazardous: truck.acceptsHazardous ?? false,
      acceptsPartialLane: truck.acceptsPartialLane ?? false,
    };
  }, [truck]);

  const [formData, setFormData] = useState(getInitialFormData() || {
    originZip: "", originCity: "", originState: "",
    destinationZip: "", destinationCity: "", destinationState: "",
    capacity: "", availableFrom: "", availableTo: "",
    dimensionLength: "", dimensionWidth: "", dimensionHeight: "",
    description: "", equipmentType: "",
    availablePallets: "", availableWeight: "", availableLinearFeet: "",
    acceptsStackable: true, acceptsHazardous: false, acceptsPartialLane: false,
  });

  useEffect(() => {
    if (open && truck) {
      const data = getInitialFormData();
      if (data) setFormData(data);
    }
  }, [open, truck, getInitialFormData]);

  const [isLookingUpOrigin, setIsLookingUpOrigin] = useState(false);
  const [isLookingUpDestination, setIsLookingUpDestination] = useState(false);

  const handleOriginZipChange = useCallback(async (zip: string) => {
    setFormData(prev => ({ ...prev, originZip: zip }));
    if (/^\d{5}$/.test(zip)) {
      setIsLookingUpOrigin(true);
      const location = await fetchLocationData(zip);
      if (location) {
        setFormData(prev => ({ ...prev, originCity: location.city, originState: location.state }));
      }
      setIsLookingUpOrigin(false);
    }
  }, []);

  const handleDestinationZipChange = useCallback(async (zip: string) => {
    setFormData(prev => ({ ...prev, destinationZip: zip }));
    if (/^\d{5}$/.test(zip)) {
      setIsLookingUpDestination(true);
      const location = await fetchLocationData(zip);
      if (location) {
        setFormData(prev => ({ ...prev, destinationCity: location.city, destinationState: location.state }));
      }
      setIsLookingUpDestination(false);
    }
  }, []);

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("PATCH", `/api/trucks/${truck?.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Truck Updated", description: "Your truck posting has been updated successfully." });
      queryClient.invalidateQueries({ queryKey: ["/api/trucks"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update truck posting.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!truck) return;

    const origin = `${formData.originCity}, ${formData.originState}`;
    const destination = `${formData.destinationCity}, ${formData.destinationState}`;

    mutation.mutate({
      origin,
      originZip: formData.originZip,
      originCity: formData.originCity,
      originState: formData.originState,
      destination,
      destinationZip: formData.destinationZip,
      destinationCity: formData.destinationCity,
      destinationState: formData.destinationState,
      capacity: parseInt(formData.capacity),
      dimensions: [formData.dimensionLength, formData.dimensionWidth, formData.dimensionHeight].filter(Boolean).join(" x ") || "Standard",
      availableFrom: new Date(formData.availableFrom).toISOString(),
      availableTo: formData.availableTo ? new Date(formData.availableTo).toISOString() : new Date(formData.availableFrom).toISOString(),
      description: formData.description,
      equipmentType: formData.equipmentType || null,
      availablePallets: formData.availablePallets ? parseInt(formData.availablePallets) : null,
      availableWeight: formData.availableWeight ? parseInt(formData.availableWeight) : null,
      availableLinearFeet: formData.availableLinearFeet ? parseInt(formData.availableLinearFeet) : null,
      acceptsStackable: formData.acceptsStackable,
      acceptsHazardous: formData.acceptsHazardous,
      acceptsPartialLane: formData.acceptsPartialLane,
    });
  };

  if (!truck) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="modal-title-edit-truck">Edit Truck Posting</DialogTitle>
          <DialogDescription>
            Update the details for your truck space listing.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Origin</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-originZip">ZIP Code *</Label>
                <div className="relative">
                  <Input
                    id="edit-originZip"
                    placeholder="30107"
                    value={formData.originZip}
                    onChange={(e) => handleOriginZipChange(e.target.value)}
                    maxLength={5}
                    data-testid="input-edit-origin-zip"
                    required
                  />
                  {isLookingUpOrigin && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-originCity">City *</Label>
                <Input
                  id="edit-originCity"
                  placeholder="City"
                  value={formData.originCity}
                  onChange={(e) => setFormData({ ...formData, originCity: e.target.value })}
                  data-testid="input-edit-origin-city"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-originState">State *</Label>
                <Input
                  id="edit-originState"
                  placeholder="State"
                  value={formData.originState}
                  onChange={(e) => setFormData({ ...formData, originState: e.target.value })}
                  data-testid="input-edit-origin-state"
                  required
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Destination</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-destinationZip">ZIP Code *</Label>
                <div className="relative">
                  <Input
                    id="edit-destinationZip"
                    placeholder="84104"
                    value={formData.destinationZip}
                    onChange={(e) => handleDestinationZipChange(e.target.value)}
                    maxLength={5}
                    data-testid="input-edit-destination-zip"
                    required
                  />
                  {isLookingUpDestination && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-destinationCity">City *</Label>
                <Input
                  id="edit-destinationCity"
                  placeholder="City"
                  value={formData.destinationCity}
                  onChange={(e) => setFormData({ ...formData, destinationCity: e.target.value })}
                  data-testid="input-edit-destination-city"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-destinationState">State *</Label>
                <Input
                  id="edit-destinationState"
                  placeholder="State"
                  value={formData.destinationState}
                  onChange={(e) => setFormData({ ...formData, destinationState: e.target.value })}
                  data-testid="input-edit-destination-state"
                  required
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="edit-acceptsPartialLane"
                checked={formData.acceptsPartialLane}
                onCheckedChange={(checked) => setFormData({ ...formData, acceptsPartialLane: checked === true })}
                data-testid="checkbox-edit-accepts-partial-lane"
              />
              <div className="space-y-1">
                <Label htmlFor="edit-acceptsPartialLane" className="cursor-pointer font-medium">Accepts Partial Lane Matches</Label>
                <p className="text-sm text-muted-foreground">Include this truck in searches and matching that share either your origin or destination, not just both</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Equipment & Capacity</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="edit-equipmentType">Equipment Type</Label>
                <Select
                  value={formData.equipmentType}
                  onValueChange={(value) => setFormData({ ...formData, equipmentType: value })}
                >
                  <SelectTrigger data-testid="select-edit-equipment-type">
                    <SelectValue placeholder="Select equipment type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-capacity">Total Capacity (lbs) *</Label>
                <FormattedNumberInput
                  id="edit-capacity"
                  decimals={0}
                  placeholder="20,000"
                  value={formData.capacity}
                  onChange={(val) => setFormData({ ...formData, capacity: val })}
                  data-testid="input-edit-capacity"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-availablePallets">Available Pallet Spots</Label>
                <FormattedNumberInput
                  id="edit-availablePallets"
                  decimals={0}
                  placeholder="e.g. 12"
                  value={formData.availablePallets}
                  onChange={(val) => setFormData({ ...formData, availablePallets: val })}
                  data-testid="input-edit-available-pallets"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-availableWeight">Available Weight (lbs)</Label>
                <FormattedNumberInput
                  id="edit-availableWeight"
                  decimals={0}
                  placeholder="e.g. 15,000"
                  value={formData.availableWeight}
                  onChange={(val) => setFormData({ ...formData, availableWeight: val })}
                  data-testid="input-edit-available-weight"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-availableLinearFeet">Available Linear Feet</Label>
                <FormattedNumberInput
                  id="edit-availableLinearFeet"
                  decimals={0}
                  placeholder="e.g. 24"
                  value={formData.availableLinearFeet}
                  onChange={(val) => setFormData({ ...formData, availableLinearFeet: val })}
                  data-testid="input-edit-available-linear-feet"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Trailer Dimensions (ft)</Label>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="edit-dimensionLength" className="text-xs text-muted-foreground">Length</Label>
                  <FormattedNumberInput
                    id="edit-dimensionLength"
                    decimals={0}
                    placeholder="e.g. 53"
                    value={formData.dimensionLength}
                    onChange={(val) => setFormData({ ...formData, dimensionLength: val })}
                    data-testid="input-edit-dimension-length"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-dimensionWidth" className="text-xs text-muted-foreground">Width</Label>
                  <FormattedNumberInput
                    id="edit-dimensionWidth"
                    placeholder="e.g. 8.5"
                    decimals={1}
                    value={formData.dimensionWidth}
                    onChange={(val) => setFormData({ ...formData, dimensionWidth: val })}
                    data-testid="input-edit-dimension-width"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-dimensionHeight" className="text-xs text-muted-foreground">Height</Label>
                  <FormattedNumberInput
                    id="edit-dimensionHeight"
                    decimals={0}
                    placeholder="e.g. 9"
                    value={formData.dimensionHeight}
                    onChange={(val) => setFormData({ ...formData, dimensionHeight: val })}
                    data-testid="input-edit-dimension-height"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-acceptsStackable"
                  checked={formData.acceptsStackable}
                  onCheckedChange={(checked) => setFormData({ ...formData, acceptsStackable: checked === true })}
                  data-testid="checkbox-edit-accepts-stackable"
                />
                <Label htmlFor="edit-acceptsStackable" className="cursor-pointer">Accepts stackable freight</Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-acceptsHazardous"
                  checked={formData.acceptsHazardous}
                  onCheckedChange={(checked) => setFormData({ ...formData, acceptsHazardous: checked === true })}
                  data-testid="checkbox-edit-accepts-hazardous"
                />
                <Label htmlFor="edit-acceptsHazardous" className="cursor-pointer">Accepts hazardous materials</Label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="edit-availableFrom">Available From *</Label>
              <Input
                id="edit-availableFrom"
                type="date"
                value={formData.availableFrom}
                onChange={(e) => setFormData({ ...formData, availableFrom: e.target.value })}
                data-testid="input-edit-available-from"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-availableTo">Available To</Label>
              <Input
                id="edit-availableTo"
                type="date"
                value={formData.availableTo}
                onChange={(e) => setFormData({ ...formData, availableTo: e.target.value })}
                data-testid="input-edit-available-to"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Additional Information</Label>
            <Textarea
              id="edit-description"
              placeholder="Any special equipment, certifications, or route details..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              data-testid="textarea-edit-description"
              rows={4}
            />
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              size="lg"
              className="flex-1"
              data-testid="button-save-truck-edit"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => onOpenChange(false)}
              data-testid="button-cancel-truck-edit"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
